import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { FormProvider, useForm } from "react-hook-form";
import { LocationType } from "types/misc";
import { useRouter } from "next/router";
import { JobPostSearchParams } from "@services/elasticsearch/utils/getJobPostSearchParamsFromServerQuery";
import { mixpanelEvent } from "@services/mixpanel";
import { AnalyticsEvent } from "@utils/redis/analytics/AnalyticsEvent";
import { JsonValue } from "type-fest";
import { AsyncAutocompleteLocation } from "@components/AsyncAutocomplete/AsyncAutocompleteLocation";
import { LocationTypeMenu } from "@components/AsyncAutocomplete/LocationTypeMenu";
import { TagsMenu } from "@components/AsyncAutocomplete/TagsMenu";
import { ParsedUrlQueryInput } from "querystring";
import { AsyncAutocompleteSearch } from "@components/AsyncAutocomplete/AsyncAutocompleteSearch";

export interface HeaderSearchModalProps {
  formValues: JobPostSearchParams;
  emptyFormValues: JobPostSearchParams;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const HeaderSearchModal = ({
  formValues,
  emptyFormValues,
  open,
  setOpen,
}: HeaderSearchModalProps) => {
  const router = useRouter();

  // Modal.
  const [tempFormValues, setTempFormValues] = useState<any | null>(null); // Used to restore form values when modal is cancelled.
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Keyboard shortcuts - listen for keys down cmd+k.
  const onKeyDown = (e) => {
    if (e.metaKey && e.which === 75) {
      setOpen(!open);
    }
  };
  useEffect(() => {
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  });

  const formMethods = useForm({
    defaultValues: formValues,
  });
  const {
    register,
    setValue,
    watch,
    handleSubmit,
    formState: { errors, isDirty },
    control,
    reset,
  } = formMethods;

  const search = watch("search");
  const location = watch("location");
  const locationType = watch("locationType");
  const tags = watch("tags");

  // Update url search params.
  const updateCurrentUrlParams = async (searchParams: JobPostSearchParams) => {
    // Tidy up params before pushing to url.
    // - Remove empty / undefined params.
    Object.keys(searchParams).forEach(
      (key) =>
        (searchParams[key] === undefined ||
          searchParams[key] === null ||
          searchParams[key] === "") &&
        delete searchParams[key]
    );

    // - Remove page param. (so we display results from page 1)
    delete searchParams["page"];
    // - Remove slug param. (not sure why this is here)
    delete searchParams["slug"];

    // Mixpanel: Record search.
    mixpanelEvent({
      name: AnalyticsEvent.SEARCH_JOB_POSTS,
      payload: {
        searchParams: searchParams as unknown as JsonValue,
      },
    });

    // Push new url to router.
    await router.push({
      pathname: "/search",
      query: searchParams as unknown as ParsedUrlQueryInput,
    });
  };

  const numberOfActiveFilters = () => {
    let count = 0;
    if (
      locationType === LocationType.ONSITE ||
      locationType === LocationType.REMOTE
    ) {
      count++;
    }
    count += tags.length;
    return count;
  };

  return (
    <div>
      <Dialog
        classes={{
          root: "",
          paper: "rounded-md border border-gray-300 shadow-md",
          scrollPaper: "items-start",
        }}
        // fullScreen={smallScreen}
        open={open}
        onClose={() => {
          reset(tempFormValues);
          setOpen(false);
        }}
        BackdropProps={{
          classes: {
            root: "backdrop-blur-sm bg-gray-400/50",
          },
        }}
      >
        <FormProvider {...formMethods}>
          <form
            onSubmit={handleSubmit(updateCurrentUrlParams)}
            style={{ width: isSmallScreen ? "100%" : 400 }}
            className="flex flex-col overflow-y-auto bg-gray-100"
          >
            {/* ------------------------------ */}
            {/* Dialog header.  */}
            {/* ------------------------------ */}
            {/* Search. */}
            <div className="bg-white border-b border-gray-300">
              <AsyncAutocompleteSearch />
            </div>

            {/* Filters */}
            <div className="">
              {/* Location. */}
              <div className="mx-4 mt-4 mb-4">
                <div className="relative">
                  <h3 className="my-2 text-xs font-bold uppercase">Location</h3>
                  <AsyncAutocompleteLocation
                    id="location"
                    value={location || null}
                    setValue={(location) => {
                      setValue("location", location);
                    }}
                  />
                  <div className="mt-2">
                    <LocationTypeMenu />
                  </div>
                </div>
              </div>

              {/* Experience. */}
              <div className="mx-4 mt-2 mb-4">
                <h3 className="my-2 text-xs font-bold uppercase">
                  Experience level
                </h3>
                <TagsMenu
                  availableTags={[
                    { id: "junior-level", label: "Junior" },
                    { id: "mid-level", label: "Mid-weight" },
                    { id: "senior-level", label: "Senior" },
                  ]}
                  name="tags"
                  type="checkbox"
                />
              </div>

              {/* Job type. */}
              <div className="mx-4 mt-2 mb-4">
                <h3 className="my-2 text-xs font-bold uppercase">Job type</h3>
                <TagsMenu
                  availableTags={[
                    { id: "full-time", label: "Full time" },
                    { id: "part-time", label: "Part time" },
                    { id: "contract", label: "Contract" },
                    { id: "internship", label: "Internship" },
                  ]}
                  name="tags"
                  type="checkbox"
                />
              </div>

              {/* Company employees. */}
              <div className="mx-4 mt-2 mb-4">
                <h3 className="my-2 text-xs font-bold uppercase">
                  Company size
                </h3>
                <TagsMenu
                  availableTags={[
                    { id: "org-size-b", label: "1-10" },
                    { id: "org-size-c", label: "11-50" },
                    { id: "org-size-d", label: "51-200" },
                    { id: "org-size-e", label: "201-500" },
                    { id: "org-size-f", label: "501-1000" },
                    { id: "org-size-g", label: "1001-5000" },
                    { id: "org-size-h", label: "5001-10,000" },
                    { id: "org-size-i", label: "10,001+" },
                  ]}
                  name="tags"
                />
              </div>

              {/* Misc. */}
              <div className="mx-4 mt-2 mb-4">
                <h3 className="my-2 text-xs font-bold uppercase">Benefits</h3>
                <TagsMenu
                  availableTags={[
                    { id: "visa-sponsorship", label: "Visa sponsorship" },
                    { id: "paid-relocation", label: "Paid relocation" },
                    { id: "4-day-week", label: "4 day week" },
                  ]}
                  name="tags"
                />
              </div>
            </div>

            {/* ------------------------------ */}
            {/* Dialog body.  */}
            {/* ------------------------------ */}

            {/* Scrollable area. */}
            <div className="overflow-y-auto">
              {/* Search suggestions. */}
              {/* <div className="border-b border-gray-300 empty:border-none">
                <AsyncSearchSuggestions id="search" value={search || null} />
              </div> */}

              {/* Filters. */}
            </div>

            {/* ------------------------------ */}
            {/* Dialog footer.  */}
            {/* ------------------------------ */}
            {/* Actions. */}
            <div className="flex flex-row justify-between p-4 text-base border-t border-gray-300 sm:text-sm">
              <button
                type="button"
                className="underline"
                onClick={() => {
                  reset(emptyFormValues);
                }}
              >
                Clear all
              </button>
              <div>
                <button
                  type="submit"
                  className="px-8 py-4 text-white bg-black rounded-md hover:text-white/90 sm:px-6 sm:py-2"
                  onClick={() => {
                    setOpen(false);
                  }}
                >
                  Search
                </button>
              </div>
            </div>
          </form>
        </FormProvider>
      </Dialog>
    </div>
  );
};
